import axios, { type AxiosInstance } from 'axios';

import { env } from '@/env';

const TIMEOUT_MS = 10000;

// Property names of POST /campaigns/v1/leads. The server rejects unknown keys with 422.
export interface CampaignsLeadBody {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: string;
  mbi?: string;
  tcAccepted?: boolean;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
  promoCode?: string;
  webregUserId?: string;
}

// Status only. An AxiosError carries the request body (name, email, DOB, MBI), so it never
// leaves this module.
export type CampaignsOutcome = { ok: true } | { ok: false; status?: number };

let instance: AxiosInstance | null = null;

const baseUrl = (): string => (env.CAMPAIGNS_SERVICE_URL ?? '').trim();

export const campaignsEnabled = (): boolean => baseUrl() !== '';

const client = (): AxiosInstance => {
  instance ??= axios.create({
    baseURL: baseUrl(),
    timeout: TIMEOUT_MS,
    // The session cookie is created by the first response and must come back on later calls.
    withCredentials: true,
  });
  return instance;
};

const settle = async (call: () => Promise<unknown>): Promise<CampaignsOutcome> => {
  try {
    await call();
    return { ok: true };
  } catch (err) {
    return { ok: false, status: axios.isAxiosError(err) ? err.response?.status : undefined };
  }
};

export const postLead = (
  body: CampaignsLeadBody,
  recaptchaToken?: string,
): Promise<CampaignsOutcome> => {
  if (!campaignsEnabled()) return Promise.resolve({ ok: false });
  const headers = recaptchaToken ? { 'X-Recaptcha-Token': recaptchaToken } : undefined;
  return settle(() => client().post('/leads', body, { headers }));
};

export const linkSession = (authToken: string): Promise<CampaignsOutcome> => {
  if (!campaignsEnabled()) return Promise.resolve({ ok: false });
  return settle(() =>
    client().post('/sessions/link', undefined, {
      headers: { Authorization: `Bearer ${authToken}` },
    }),
  );
};
